import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';

import type { FileAttachment } from '../../../../hooks/use-file-selection';

/**
 * Context for sharing attachment state across components.
 */
interface AttachmentsContextValue {
  attachments: Array<FileAttachment>;
  setAttachments: React.Dispatch<React.SetStateAction<Array<FileAttachment>>>;
  addAttachments: (newAttachments: Array<FileAttachment>) => void;
  removeAttachment: (index: number) => void;
  clearAttachments: () => void;
  updateAttachment: (index: number, updates: Partial<FileAttachment>) => void;
}

const AttachmentsContext = createContext<AttachmentsContextValue | undefined>(undefined);

type AttachmentsProviderProps = { children: ReactNode } & (
  | {
      attachments: Array<FileAttachment>;
      setAttachments: Dispatch<SetStateAction<Array<FileAttachment>>>;
    }
  | {
      attachments?: undefined;
      setAttachments?: undefined;
    }
);

/**
 * Provider for attachment state.
 * This allows multiple components (ChatAttachments, ChatAttachmentsPreview, etc.)
 * to share the same attachment state.
 */
export function AttachmentsProvider({
  children,
  attachments: controlledAttachments,
  setAttachments: controlledSetAttachments,
}: AttachmentsProviderProps) {
  const [internalAttachments, setInternalAttachments] = useState<Array<FileAttachment>>([]);
  const attachments = controlledAttachments ?? internalAttachments;
  const setAttachments = controlledSetAttachments ?? setInternalAttachments;

  const addAttachments = useCallback(
    (newAttachments: Array<FileAttachment>) => {
      setAttachments((prev) => [...prev, ...newAttachments]);
    },
    [setAttachments],
  );

  const removeAttachment = useCallback(
    (index: number) => {
      setAttachments((prev) => prev.filter((_, i) => i !== index));
    },
    [setAttachments],
  );

  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, [setAttachments]);

  const updateAttachment = useCallback(
    (index: number, updates: Partial<FileAttachment>) => {
      setAttachments((prev) => prev.map((attachment, i) => (i === index ? { ...attachment, ...updates } : attachment)));
    },
    [setAttachments],
  );

  const value = useMemo(
    () => ({
      attachments,
      setAttachments,
      addAttachments,
      removeAttachment,
      clearAttachments,
      updateAttachment,
    }),
    [attachments, setAttachments, addAttachments, removeAttachment, clearAttachments, updateAttachment],
  );

  return <AttachmentsContext.Provider value={value}>{children}</AttachmentsContext.Provider>;
}

/**
 * Hook for accessing attachment state.
 * Must be used within AttachmentsProvider.
 */
export function useAttachments(): AttachmentsContextValue {
  const context = useContext(AttachmentsContext);
  if (!context) {
    throw new Error('useAttachments must be used within AttachmentsProvider');
  }
  return context;
}
